import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Users, Plus, Mail, Phone, ArrowLeft, ChevronRight, Pencil, Trash2, Wrench, Upload, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, EmptyState, Skeleton } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { Avatar } from '@/components/ui/avatar';
import { PageHeader } from '@/components/ui/page-header';
import { Modal } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { api, formatDate, ApiError, cn, formatCurrency } from '@/lib/utils';
import { EQUIPMENT_TYPES } from '@maintenance-tracker/shared';
import { CsvImportModal } from '@/legacy/components/csv-import-modal';

interface CustomerFields {
  name: string;
  email: string;
  phone: string;
  company: string;
  address: string;
  notes: string;
}

const EMPTY_CUSTOMER: CustomerFields = { name: '', email: '', phone: '', company: '', address: '', notes: '' };

interface EquipmentFields {
  type: (typeof EQUIPMENT_TYPES)[number];
  name: string;
  install_year: number;
  notes: string;
}

const EMPTY_EQUIPMENT: EquipmentFields = {
  type: 'furnace',
  name: '',
  install_year: new Date().getFullYear() - 12,
  notes: '',
};

export function CustomersPage() {
  const [importOpen, setImportOpen] = useState(false);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api<{ contacts: Array<{ id: string; name: string; email: string; phone: string }> }>('/api/v1/contacts'),
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Customers"
        description="Everyone you hold a maintenance agreement with."
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" /> Import from spreadsheet
            </Button>
            <Link to="/customers/new">
              <Button>
                <Plus className="h-4 w-4" /> Add customer
              </Button>
            </Link>
          </div>
        }
      />
      <CsvImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['customers'] })}
        defaultKind="customers"
        allowedKinds={['customers', 'agreements', 'services', 'equipment']}
      />
      <Card noPadding>
        <div className="scroll-slim overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-bg-muted text-left text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                <th className="px-5 py-3">Name</th>
                <th className="px-2 py-3">Email</th>
                <th className="px-2 py-3">Phone</th>
                <th className="w-10 px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <Skeleton className="h-9 w-9 rounded-full" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                    </td>
                    <td className="px-2 py-3"><Skeleton className="h-4 w-40" /></td>
                    <td className="px-2 py-3"><Skeleton className="h-4 w-24" /></td>
                    <td className="px-5 py-3" />
                  </tr>
                ))
              ) : data?.contacts?.length ? (
                data.contacts.map((c) => (
                  <tr key={c.id} className="group transition-colors hover:bg-bg-subtle/60">
                    <td className="px-5 py-3">
                      <Link to="/customers/$id" params={{ id: c.id }} className="flex items-center gap-2.5">
                        <Avatar name={c.name} size="md" />
                        <span className="font-medium text-text-primary group-hover:text-brand">{c.name}</span>
                      </Link>
                    </td>
                    <td className="px-2 py-3 text-text-secondary">{c.email ?? '—'}</td>
                    <td className="px-2 py-3 text-text-secondary">{c.phone ?? '—'}</td>
                    <td className="px-5 py-3 text-right">
                      <Link to="/customers/$id" params={{ id: c.id }}>
                        <ChevronRight className="ml-auto h-4 w-4 text-text-muted" />
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>
                    <EmptyState
                      icon={Users}
                      title="No customers yet"
                      description="Add customers so you can attach maintenance agreements to them."
                      action={
                        <Link to="/customers/new">
                          <Button>
                            <Plus className="h-4 w-4" /> Add customer
                          </Button>
                        </Link>
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export function NewCustomerPage() {
  return <CustomerForm />;
}

interface CustomerContact {
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  address?: string;
  notes?: string;
}

export function CustomerDetailPage({ id }: { id: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  const [equipmentForm, setEquipmentForm] = useState<EquipmentFields>(EMPTY_EQUIPMENT);
  const [form, setForm] = useState<CustomerFields>(EMPTY_CUSTOMER);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['customer', id],
    queryFn: () =>
      api<{
        contact: CustomerContact;
        agreements: Array<{ id: string; title: string; status: string; endDate: string }>;
        services: Array<{ id: string; title: string; serviceDate: string | null; amount: number; notes: string | null }>;
      }>(`/api/v1/contacts/${id}`),
  });

  const { data: equipmentData, isLoading: equipmentLoading } = useQuery({
    queryKey: ['customer-equipment', id],
    queryFn: () =>
      api<{
        items: Array<{
          id: string;
          type: string;
          name: string;
          installYear: number;
          expectedLifeYears: number;
          lifePercent: number;
          notes: string | null;
        }>;
      }>(`/api/v1/contacts/${id}/equipment`),
  });

  const addEquipment = useMutation({
    mutationFn: () =>
      api(`/api/v1/contacts/${id}/equipment`, {
        method: 'POST',
        body: JSON.stringify(equipmentForm),
      }),
    onSuccess: () => {
      setEquipmentOpen(false);
      setEquipmentForm(EMPTY_EQUIPMENT);
      qc.invalidateQueries({ queryKey: ['customer-equipment', id] });
      qc.invalidateQueries({ queryKey: ['upgrades'] });
    },
  });

  const removeEquipment = useMutation({
    mutationFn: (equipmentId: string) => api(`/api/v1/equipment/${equipmentId}`, { method: 'DELETE', body: '{}' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-equipment', id] });
      qc.invalidateQueries({ queryKey: ['upgrades'] });
    },
  });

  const openEdit = () => {
    const c = data?.contact;
    setForm({
      name: c?.name ?? '',
      email: c?.email ?? '',
      phone: c?.phone ?? '',
      company: c?.company ?? '',
      address: c?.address ?? '',
      notes: c?.notes ?? '',
    });
    setEditOpen(true);
  };

  const save = useMutation({
    mutationFn: () =>
      api(`/api/v1/contacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ ...form, email: form.email || undefined }),
      }),
    onSuccess: () => {
      setEditOpen(false);
      qc.invalidateQueries({ queryKey: ['customer', id] });
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  const remove = useMutation({
    mutationFn: () => api(`/api/v1/contacts/${id}`, { method: 'DELETE', body: '{}' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      navigate({ to: '/customers' });
    },
    onError: (e) => {
      setDeleteError(
        e instanceof ApiError && e.code === 'HAS_AGREEMENTS'
          ? 'This customer still has active agreements. Archive them first, then delete.'
          : 'Could not delete this customer. Please try again.',
      );
    },
  });

  return (
    <div className="space-y-5">
      <Link to="/customers" className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary">
        <ArrowLeft className="h-4 w-4" /> All customers
      </Link>

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar name={data?.contact?.name ?? '?'} size="lg" />
          <div>
            <h1 className="text-[22px] font-semibold tracking-tight">{data?.contact?.name ?? 'Customer'}</h1>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-text-secondary">
              {data?.contact?.email && (
                <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {data.contact.email}</span>
              )}
              {data?.contact?.phone && (
                <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {data.contact.phone}</span>
              )}
            </div>
          </div>
        </div>
        {data?.contact && (
          <div className="flex shrink-0 gap-2">
            <Button variant="secondary" size="sm" onClick={openEdit}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDeleteError(null);
                setDeleteOpen(true);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>
          </div>
        )}
      </div>

      <Card
        title="Equipment"
        description="Track install years for furnaces, AC units, and more — used to flag replacement opportunities."
        action={
          <Button variant="secondary" size="sm" onClick={() => setEquipmentOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> Add equipment
          </Button>
        }
        noPadding
      >
        {equipmentLoading ? (
          <div className="space-y-3 p-5">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-12 rounded-lg" />
            ))}
          </div>
        ) : equipmentData?.items?.length ? (
          <ul className="divide-y divide-border">
            {equipmentData.items.map((eq) => (
              <li key={eq.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Wrench className="h-3.5 w-3.5 text-text-muted" />
                    <p className="text-sm font-medium">{eq.name}</p>
                    <span className="rounded-full bg-bg-subtle px-2 py-0.5 text-[11px] capitalize text-text-secondary">
                      {eq.type.replace('_', ' ')}
                    </span>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[11px] font-medium',
                        eq.lifePercent >= 85 ? 'bg-brand-subtle text-brand' : 'bg-bg-subtle text-text-secondary',
                      )}
                    >
                      {eq.lifePercent}% life used
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-text-muted">Installed {eq.installYear}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={removeEquipment.isPending}
                  onClick={() => removeEquipment.mutate(eq.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 py-8 text-center text-sm text-text-muted">No equipment on file yet.</p>
        )}
      </Card>

      <Card title="Agreements" noPadding>
        {isLoading ? (
          <div className="space-y-3 p-5">
            {[1, 2].map((i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
          </div>
        ) : data?.agreements?.length ? (
          <ul className="divide-y divide-border">
            {data.agreements.map((a) => (
              <li key={a.id} className="flex items-center justify-between px-5 py-3.5">
                <div>
                  <p className="text-sm font-medium">{a.title}</p>
                  <p className="text-xs text-text-muted">Renews {a.endDate ? formatDate(a.endDate) : '—'}</p>
                </div>
                <StatusBadge status={a.status} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 py-8 text-center text-sm text-text-muted">No agreements for this customer yet.</p>
        )}
      </Card>

      <Card title="Service history" description="Completed visits imported or synced from your field software." noPadding>
        {isLoading ? (
          <div className="space-y-3 p-5">
            {[1, 2].map((i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
          </div>
        ) : data?.services?.length ? (
          <ul className="divide-y divide-border">
            {data.services.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-3.5 w-3.5 shrink-0 text-text-muted" />
                    <p className="truncate text-sm font-medium">{s.title}</p>
                  </div>
                  <p className="mt-0.5 text-xs text-text-muted">
                    {s.serviceDate ? formatDate(s.serviceDate) : 'Date unknown'}
                    {s.notes ? ` · ${s.notes}` : ''}
                  </p>
                </div>
                {s.amount > 0 && (
                  <span className="shrink-0 text-sm font-medium tabular-nums">{formatCurrency(s.amount)}</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 py-8 text-center text-sm text-text-muted">
            No service visits on file. Import from a spreadsheet or connect an integration.
          </p>
        )}
      </Card>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit customer">
        <div className="space-y-4">
          <CustomerFieldset form={form} setForm={setForm} />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!form.name.trim()}>
              Save changes
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={equipmentOpen} onClose={() => setEquipmentOpen(false)} title="Add equipment">
        <div className="space-y-4">
          <Input
            label="Name"
            value={equipmentForm.name}
            onChange={(e) => setEquipmentForm({ ...equipmentForm, name: e.target.value })}
            placeholder="Carrier 80% furnace"
            required
          />
          <label className="block text-sm font-medium text-text-primary">
            Type
            <select
              className="mt-1.5 w-full rounded-lg border border-border bg-bg-app px-3 py-2 text-sm"
              value={equipmentForm.type}
              onChange={(e) =>
                setEquipmentForm({ ...equipmentForm, type: e.target.value as EquipmentFields['type'] })
              }
            >
              {EQUIPMENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replace('_', ' ')}
                </option>
              ))}
            </select>
          </label>
          <Input
            label="Install year"
            type="number"
            value={equipmentForm.install_year}
            onChange={(e) => setEquipmentForm({ ...equipmentForm, install_year: Number(e.target.value) || 2000 })}
          />
          <Input
            label="Notes"
            value={equipmentForm.notes}
            onChange={(e) => setEquipmentForm({ ...equipmentForm, notes: e.target.value })}
          />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => setEquipmentOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => addEquipment.mutate()}
              loading={addEquipment.isPending}
              disabled={!equipmentForm.name.trim()}
            >
              Save equipment
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete customer?"
        description="This permanently removes the customer. This cannot be undone."
      >
        <div className="space-y-4">
          {deleteError && (
            <p className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">{deleteError}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => remove.mutate()} loading={remove.isPending}>
              Delete customer
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function CustomerFieldset({
  form,
  setForm,
}: {
  form: CustomerFields;
  setForm: (f: CustomerFields) => void;
}) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <Input label="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
      </div>
      <Input label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
      <Input label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
    </>
  );
}

function CustomerForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<CustomerFields>(EMPTY_CUSTOMER);

  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/api/v1/contacts', {
        method: 'POST',
        body: JSON.stringify({ ...form, email: form.email || undefined }),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      navigate({ to: '/customers/$id', params: { id: res.id } });
    },
  });

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <PageHeader title="Add customer" description="Add a customer now, then attach maintenance agreements to them." />
      <Card>
        <div className="space-y-4">
          <CustomerFieldset form={form} setForm={setForm} />
          <div className="flex items-center justify-between pt-1">
            <Link
              to="/agreements/new"
              className="inline-flex items-center gap-1.5 text-[13px] text-text-secondary hover:text-text-primary"
            >
              <Plus className="h-3.5 w-3.5" /> Add with an agreement instead
            </Link>
            <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!form.name.trim()}>
              Save customer
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
